import { isSameDay } from 'date-fns';
import type { DateObj } from 'dayzed';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import type { CalendarProps } from './Calendar';
import { Calendar } from './Calendar';

export type DateRangePickerProps = {
  selectedDate: Date[];
  onDateSelected: (date: Date[]) => void;
  dateForInitialView?: Date;
} & Omit<CalendarProps, 'onSelectionStatus' | 'selected' | 'onDateSelected'>;
export const DateRangePicker: FC<DateRangePickerProps> = ({
  selectedDate,
  onDateSelected,
  dateForInitialView,
  ...calendarProps
}) => {
  const [focusedDateObj, setFocusedDateObj] = useState<DateObj | null>(null);

  const onSelectionStatus = useCallback(
    (dateObj: DateObj) => {
      if (!selectedDate.length) {
        return 'none';
      }

      const selectedTimes = selectedDate.map((v) => v.getTime());
      const dateObjTime = dateObj.date.getTime();

      if (selectedTimes.every((v) => isSameDay(dateObjTime, v))) {
        return 'single';
      }

      if (selectedDate.length === 1) {
        if (
          focusedDateObj &&
          ((dateObjTime > selectedTimes[0] &&
            dateObjTime < focusedDateObj?.date.getTime()) ||
            (dateObjTime < selectedTimes[0] &&
              dateObjTime > focusedDateObj?.date.getTime()))
        ) {
          return 'hover';
        }
        return 'none';
      }

      if (isSameDay(dateObjTime, selectedTimes[0])) {
        return 'start';
      }

      if (isSameDay(dateObjTime, selectedTimes[1])) {
        return 'end';
      }

      if (dateObjTime > selectedTimes[0] && dateObjTime < selectedTimes[1]) {
        return 'mid';
      }

      return 'none';
    },
    [focusedDateObj, selectedDate],
  );

  return (
    <Calendar
      dateForInitialView={dateForInitialView}
      selected={selectedDate}
      onSelectionStatus={onSelectionStatus}
      onDateSelected={(dateObj) => {
        if (!dateObj.selectable) {
          return;
        }

        if (selectedDate.length === 1) {
          if (selectedDate[0].getTime() < dateObj.date.getTime()) {
            onDateSelected([selectedDate[0], dateObj.date]);
          } else {
            onDateSelected([dateObj.date, selectedDate[0]]);
          }
          return;
        }

        onDateSelected([dateObj.date]);
      }}
      onMouseMoveOnCell={(dateObj) => {
        setFocusedDateObj(dateObj);
      }}
      onMouseOut={() => {
        setFocusedDateObj(null);
      }}
      {...calendarProps}
    />
  );
};
